import {
  Box,
  Flex,
  Icon,
  IconButton,
  Text,
  VStack,
  Button,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addJob, fetchSkillList, fetchRoleList, fetchAccountabilityAreaList } from '../../api/classification/classification'
import { PlusIcon, Trash04Icon, WarningIcon } from '../../assets/icons'
import { countrySelectOptions } from '../countries/PhoneDial'
import ModalInput from '../forms/modalForms/modalInput'
import ModalInputSelect from '../forms/modalForms/modalInputSelect'
import ModalSelect from '../forms/modalForms/modalSelect'
import ModalMultiSelect from '../forms/modalForms/modalMultiSelect'
import { currencyMap } from '../../helpers/currencyMap'
import currencySymbolMap from 'currency-symbol-map'
import FormModal from '../common/FormModal'
import { useFormMutation } from '../../hooks/useFormMutation'

interface AddJobsModalProps {
  isOpen: boolean
  onClose: () => void
  level: string
}

type SkillType = 'skill' | 'competence' | 'task'

const skillLevelValuesMap: Record<SkillType, string[]> = {
  competence: ['foundational', 'operational', 'tactical', 'strategic'],
  task: ['assisted', 'independent', 'complex', 'leadership'],
  skill: ['basic', 'intermediate', 'advanced', 'specialist'],
}

const AddJobsModal: React.FC<AddJobsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()

  const getSkillLevelOptions = (skillType: SkillType) =>
    (skillLevelValuesMap[skillType] ?? []).map((value) => ({
      value,
      label: t(`classifications.behaviourLevels.${value}`),
    }))

  const getSkillLevelLabel = (_skillType: SkillType, value: string) =>
    t(`classifications.behaviourLevels.${value}`, { defaultValue: value })

  const emptyData = {
    jobTitle: '',
    description: '',
    jobLevel: ['No Levels'] as Array<'No Levels' | 'Junior' | 'Medior' | 'Senior'>,
    skills: [] as {
      skillType: SkillType
      skillName: string | null
      skillId: string | null
      skillLevels: string[]
    }[],
    roles: [] as { roleId: string; roleName: string }[],
    accountabilityAreas: [] as { areaId: string; areaName: string; raciLabels: string[] }[],
    countries: [] as { country: string; minimum_salary: string; maximum_salary: string; currency?: string }[],
  }

  const emptySkill = {
    skillType: 'skill' as SkillType,
    skillName: '' as string | null,
    skillId: '' as string | null,
    skillLevels: [] as string[],
  }

  const emptyErrors = {
    jobTitle: false,
    description: false,
    skills: false,
    minSalary: false,
    maxSalary: false,
    country: false,
  }

  const emptySkillErrors = {
    skillType: false,
    skillId: false,
    skillLevel: false,
  }

  const emptyArea = {
    areaId: null as string | null,
    areaName: null as string | null,
    raciLabels: [] as string[],
  }

  const emptyAreaErrors = {
    areaId: false,
    raciLabels: false,
  }

  const [data, setData] = useState(emptyData)
  const [currentSkill, setCurrentSkill] = useState(emptySkill)
  const [currentArea, setCurrentArea] = useState(emptyArea)
  const [errors, setErrors] = useState(emptyErrors)
  const [skillErrors, setSkillErrors] = useState(emptySkillErrors)
  const [areaErrors, setAreaErrors] = useState(emptyAreaErrors)
  const [skillSearch, setSkillSearch] = useState('')
  const [roleSearch, setRoleSearch] = useState('')
  const [areaSearch, setAreaSearch] = useState('')
  const [selectedJobLevels, setSelectedJobLevels] = useState<string[] | null>(null)

  const resetForm = () => {
    setData(emptyData)
    setCurrentSkill(emptySkill)
    setCurrentArea(emptyArea)
    setErrors(emptyErrors)
    setSkillErrors(emptySkillErrors)
    setAreaErrors(emptyAreaErrors)
    setSkillSearch('')
    setRoleSearch('')
    setAreaSearch('')
    setSelectedJobLevels(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const createJobMutation = useFormMutation({
    mutationFn: addJob,
    queryKey: 'jobs',
    successMessage: t('classifications.toasts.jobCreatedSuccessMessage'),
    onClose: handleClose,
  })

  const skills = useQuery({
    queryKey: ['skills', skillSearch, currentSkill.skillType],
    queryFn: () =>
      fetchSkillList({ search: skillSearch, type: currentSkill.skillType, page: 1, pageSize: 5, filters: {} }),
  })

  const rolesQuery = useQuery({
    queryKey: ['roles-select', roleSearch],
    queryFn: () => fetchRoleList({ search: roleSearch, page: 1, pageSize: 10 }),
  })

  const areasQuery = useQuery({
    queryKey: ['areas-select', areaSearch],
    queryFn: () => fetchAccountabilityAreaList({ search: areaSearch, page: 1, pageSize: 10 }),
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setData((prev) => ({ ...prev, [name]: value }))
    setErrors({ ...errors, [name]: '' })
  }

  const isValid = () => {
    const newErrors = {
      jobTitle: data.jobTitle === '',
      description: data.jobTitle === '',
      skills: data.skills.length === 0,
      minSalary: data.countries.some(
        (country) => isNaN(Number(country.minimum_salary)) || country.minimum_salary === '',
      ),
      maxSalary: data.countries.some(
        (country) => isNaN(Number(country.maximum_salary)) || country.maximum_salary === '',
      ),
      country: false,
    }
    setErrors(newErrors)
    return !Object.values(newErrors).some((error) => error)
  }

  const isSkillValid = () => {
    const newErrors = {
      skillType: false,
      skillId: !currentSkill.skillId,
      skillLevel: currentSkill.skillLevels.length === 0,
    }
    setSkillErrors(newErrors)
    return !Object.values(newErrors).some((error) => error)
  }

  const handleAddSkill = () => {
    if (isSkillValid()) {
      setData({ ...data, skills: [...data.skills, currentSkill] })
      setCurrentSkill(emptySkill)
      setSkillSearch('')
    }
  }

  const isAreaValid = () => {
    const newErrors = {
      areaId: !currentArea.areaId,
      raciLabels: currentArea.raciLabels.length === 0,
    }
    setAreaErrors(newErrors)
    return !Object.values(newErrors).some((error) => error)
  }

  const handleAddArea = () => {
    if (isAreaValid()) {
      setData({
        ...data,
        accountabilityAreas: [
          ...data.accountabilityAreas,
          { areaId: currentArea.areaId!, areaName: currentArea.areaName!, raciLabels: currentArea.raciLabels },
        ],
      })
      setCurrentArea(emptyArea)
      setAreaSearch('')
    }
  }

  const handleSubmit = () => {
    if (isValid()) {
      createJobMutation.mutate({
        title: data.jobTitle,
        description: data.description,
        skills: data.skills.map((skill) => ({
          skill: skill.skillId as string,
          competency_levels: skill.skillLevels,
        })),
        job_profile_roles: data.roles.map((r) => ({ role: r.roleId })),
        job_profile_accountability_areas: data.accountabilityAreas.map((a) => ({
          accountability_area: a.areaId,
          raci_labels: a.raciLabels,
        })),
        countries: data.countries.map((country) => ({
          ...country,
          country: country.country.toUpperCase(),
        })),
        job_levels: selectedJobLevels || [],
      } as any)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('classifications.actions.addNewJobProfile')}
      onSubmit={handleSubmit}
      isLoading={createJobMutation.isPending}
      size="2xl"
    >
      <VStack spacing={3} align="stretch" w="100%">
        <Text textStyle="h-ov" color="solid.600" borderBottomColor="solid.50" borderBottomWidth="1px" mt={3}>
          {t('classifications.sections.generalInformation').toUpperCase()}
        </Text>
        <Box width="100%">
          <ModalInput
            label={t('common.fields.jobTitle')}
            type="text"
            name="jobTitle"
            placeholder={t('classifications.placeholders.addJobTitle')}
            value={data.jobTitle}
            onChange={handleChange}
            isInvalid={!!errors.jobTitle}
          />
          {errors.jobTitle && (
            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
              <Text>
                <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                  <WarningIcon />
                </Icon>
                {t('classifications.validation.enterJobTitle')}
              </Text>
            </Box>
          )}
        </Box>
        <Box width="100%">
          <ModalInput
            label={t('common.fields.description')}
            type="textarea"
            height="128px"
            name="description"
            placeholder={t('classifications.placeholders.addDescription')}
            value={data.description}
            onChange={handleChange}
            isInvalid={!!errors.description}
          />
          {errors.description && (
            <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
              <Text>
                <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                  <WarningIcon />
                </Icon>
                {t('classifications.validation.enterDescription')}
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
      <VStack spacing={3} align="stretch" w="100%">
        <Text textStyle="h-ov" color="solid.600" borderBottomColor="solid.50" borderBottomWidth="1px" mt={3}>
          {t('classifications.sections.jobLevels').toUpperCase()}
        </Text>
        <Flex w="100%" gap={4}>
          {([
            { key: 'none', label: t('classifications.levels.noLevels') },
            { key: 'junior', label: t('classifications.levels.junior') },
            { key: 'medior', label: t('classifications.levels.medior') },
            { key: 'senior', label: t('classifications.levels.senior') },
          ] as const).map((levelOption) => {
            const isSelected =
              levelOption.key === 'none'
                ? selectedJobLevels === null
                : selectedJobLevels !== null && selectedJobLevels.includes(levelOption.key)
            return (
              <Button
                key={levelOption.key}
                flex={1}
                variant={isSelected ? 'mono' : 'notSelected'}
                onClick={() => {
                  if (levelOption.key === 'none') {
                    setSelectedJobLevels(null)
                  } else {
                    let updatedLevels: string[] = selectedJobLevels ? [...selectedJobLevels] : []
                    if (updatedLevels.includes(levelOption.key)) {
                      updatedLevels = updatedLevels.filter((l) => l !== levelOption.key)
                    } else {
                      updatedLevels.push(levelOption.key)
                    }
                    setSelectedJobLevels(updatedLevels.length ? updatedLevels : null)
                  }
                }}
              >
                {levelOption.label}
              </Button>
            )
          })}
        </Flex>
      </VStack>
      <VStack spacing={3} align="stretch" w="100%">
        <Text textStyle="h-ov" color="solid.600" borderBottomColor="solid.50" borderBottomWidth="1px" mt={3}>
          {t('classifications.sections.skillsCompetencesTasks').toUpperCase()}
        </Text>
        <Box width="100%">
          {data.skills.map((skill, i) => (
            <Flex justifyContent="space-between" key={`skill-${i}`} mb="12px">
              <Flex gap="8px" flex="1">
                <Box flex="1">
                  <ModalSelect
                    name="skillType"
                    value={skill.skillType}
                    options={[
                      { value: 'skill', label: t('content.types.skills') },
                      { value: 'competence', label: t('content.types.competence') },
                      { value: 'task', label: t('content.types.task') },
                    ]}
                    onChange={() => {}}
                    searchDisabled={true}
                    isDisabled
                  />
                </Box>
                <Box>
                  <ModalInputSelect
                    placeholder={t('common.labels.searchPlaceholder')}
                    options={[]}
                    value={{ value: skill.skillId ?? '', label: skill.skillName ?? '' }}
                    search=""
                    onSearchChange={() => {}}
                    onChange={() => {}}
                    isDisabled
                  />
                </Box>
                <Box>
                  <ModalMultiSelect
                    options={getSkillLevelOptions(skill.skillType).map((option) => ({
                      key: option.value,
                      name: option.label,
                    }))}
                    selectedValues={skill.skillLevels.map((value) => ({
                      key: value,
                      name: getSkillLevelLabel(skill.skillType, value),
                    }))}
                    onChange={() => {}}
                    searchDisabled={true}
                    isDisabled
                  />
                </Box>
              </Flex>
              <IconButton
                onClick={() =>
                  setData({ ...data, skills: data.skills.filter((_, index) => index !== i) })
                }
                aria-label={t('common.actions.remove')}
                variant="error"
                ml={2}
                icon={
                  <Icon w="24px" h="24px" viewBox="0 0 24 24" fill="none">
                    <Trash04Icon />
                  </Icon>
                }
              />
            </Flex>
          ))}
          <Flex w="100%" justifyContent="space-between" alignItems="flex-start">
            <Flex gap="8px" flex="1">
              <Box minW="150px">
                <ModalSelect
                  label={t('classifications.fields.selectType')}
                  name="skillType"
                  value={currentSkill.skillType}
                  options={[
                    { value: 'skill', label: t('content.types.skills') },
                    { value: 'competence', label: t('content.types.competence') },
                    { value: 'task', label: t('content.types.task') },
                  ]}
                  onChange={(e) => {
                    setSkillSearch('')
                    setCurrentSkill({
                      ...currentSkill,
                      skillType: e.target.value as SkillType,
                      skillLevels: [],
                    })
                  }}
                  searchDisabled={true}
                />
              </Box>
              <Box flex="1">
                <ModalInputSelect
                  placeholder={t('common.labels.searchPlaceholder')}
                  label={t('classifications.fields.selectSkillByType', {
                    type:
                      currentSkill.skillType === 'skill'
                        ? t('content.types.skills')
                        : currentSkill.skillType === 'task'
                          ? t('content.types.task')
                          : t('content.types.competence'),
                  })}
                  options={skills.data?.data?.map((skill: { id: string; name: string }) => ({
                    value: skill.id,
                    label: skill.name,
                  }))}
                  value={
                    currentSkill.skillId
                      ? { value: currentSkill.skillId as string, label: currentSkill.skillName as string }
                      : null
                  }
                  search={skillSearch}
                  isLoading={skills.isLoading}
                  onSearchChange={(v) => setSkillSearch(v)}
                  onChange={(value) => {
                    if (value) {
                      setCurrentSkill({ ...currentSkill, skillName: value.label, skillId: value.value })
                    } else {
                      setCurrentSkill({ ...currentSkill, skillName: null, skillId: null })
                    }
                  }}
                />
                {skillErrors.skillId && (
                  <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                    <Text>
                      <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                        <WarningIcon />
                      </Icon>
                      {t('classifications.validation.invalidSelection')}
                    </Text>
                  </Box>
                )}
              </Box>
              <Box>
                <ModalMultiSelect
                  label={t('classifications.fields.skillLevel')}
                  options={getSkillLevelOptions(currentSkill.skillType).map((option) => ({
                    key: option.value,
                    name: option.label,
                  }))}
                  selectedValues={currentSkill.skillLevels.map((value) => ({
                    key: value,
                    name: getSkillLevelLabel(currentSkill.skillType, value),
                  }))}
                  placeholder={t('common.placeholders.selectOption')}
                  searchDisabled={true}
                  onChange={(selected) => {
                    setCurrentSkill({
                      ...currentSkill,
                      skillLevels: selected.map((opt) => opt.key),
                    })
                  }}
                />
                {skillErrors.skillLevel && (
                  <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                    <Text>
                      <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                        <WarningIcon />
                      </Icon>
                      {t('classifications.validation.selectAtLeastOne')}
                    </Text>
                  </Box>
                )}
              </Box>
            </Flex>
            <IconButton
              mt="22px"
              aria-label={t('common.actions.add')}
              variant="mono"
              ml={2}
              onClick={handleAddSkill}
              icon={
                <Icon w="24px" h="24px" viewBox="0 0 24 24">
                  <PlusIcon />
                </Icon>
              }
            />
          </Flex>
        </Box>
        {errors.skills && (
          <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
            <Text>
              <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                <WarningIcon />
              </Icon>
              {t('classifications.validation.addAtLeastOneSkill')}
            </Text>
          </Box>
        )}
      </VStack>
      <VStack spacing={3} align="stretch" w="100%">
        <Text textStyle="h-ov" color="solid.600" borderBottomColor="solid.50" borderBottomWidth="1px" mt={3}>
          {t('classifications.sections.roles').toUpperCase()}
        </Text>
        <Box width="100%">
          {data.roles.length > 0 && (
            <Box borderRadius="8px" border="1px solid" borderColor="solid.100" overflow="hidden" mb={3}>
              {data.roles.map((role, i) => (
                <Flex
                  key={`role-${i}`}
                  justifyContent="space-between"
                  alignItems="center"
                  px={4}
                  py={3}
                  borderBottom={i < data.roles.length - 1 ? '1px solid' : 'none'}
                  borderColor="solid.50"
                  _hover={{ bg: 'solid.25' }}
                >
                  <Text textStyle="l-xs" color="solid.900">{role.roleName}</Text>
                  <IconButton
                    aria-label={t('common.actions.remove')}
                    variant="ghost"
                    size="xs"
                    minW="auto"
                    h="auto"
                    p={1}
                    color="solid.400"
                    _hover={{ color: 'error.100', bg: 'transparent' }}
                    onClick={() => setData({ ...data, roles: data.roles.filter((_, index) => index !== i) })}
                    icon={<Icon w="14px" h="14px" viewBox="0 0 24 24" fill="none"><Trash04Icon /></Icon>}
                  />
                </Flex>
              ))}
            </Box>
          )}
          <Box w="100%">
            <ModalSelect
              label={t('classifications.sections.roles')}
              name="role"
              value=""
              options={
                (rolesQuery.data?.data || [])
                  .filter((r: { id: string; title: string }) => !data.roles.some((dr) => dr.roleId === r.id))
                  .map((r: { id: string; title: string }) => ({ value: r.id, label: r.title }))
              }
              onChange={(e) => {
                const roleId = e.target.value
                if (!roleId) return
                const role = rolesQuery.data?.data?.find((r: { id: string }) => r.id === roleId)
                if (role) {
                  setData((prev) => ({
                    ...prev,
                    roles: [...prev.roles, { roleId: role.id, roleName: role.title }],
                  }))
                }
              }}
              placeholder={t('common.placeholders.selectOption')}
              onSearch={(v) => setRoleSearch(v)}
              isLoading={rolesQuery.isLoading}
            />
          </Box>
        </Box>
      </VStack>
      <VStack spacing={3} align="stretch" w="100%">
        <Text textStyle="h-ov" color="solid.600" borderBottomColor="solid.50" borderBottomWidth="1px" mt={3}>
          {t('classifications.sections.accountabilityAreas').toUpperCase()}
        </Text>
        <Box width="100%">
          {data.accountabilityAreas.length > 0 && (
            <Box borderRadius="8px" border="1px solid" borderColor="solid.100" overflow="hidden" mb={3}>
              {data.accountabilityAreas.map((area, i) => (
                <Flex
                  key={`area-${i}`}
                  justifyContent="space-between"
                  alignItems="center"
                  px={4}
                  py={3}
                  gap={4}
                  borderBottom={i < data.accountabilityAreas.length - 1 ? '1px solid' : 'none'}
                  borderColor="solid.50"
                  _hover={{ bg: 'solid.25' }}
                >
                  <Text textStyle="l-xs" color="solid.900" flex="1" minW="0">
                    {area.areaName}
                  </Text>
                  <Flex gap={1} wrap="wrap" flex="1" justifyContent="flex-end" alignItems="center">
                    {area.raciLabels.map((label) => (
                      <Box key={label} px={2} py={0.5} borderRadius="md" bg="solid.50">
                        <Text textStyle="l-xs" color="solid.600">
                          {t(`classifications.raci.${label}`)}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                  <IconButton
                    aria-label={t('common.actions.remove')}
                    variant="ghost"
                    size="xs"
                    minW="auto"
                    h="auto"
                    p={1}
                    color="solid.400"
                    _hover={{ color: 'error.100', bg: 'transparent' }}
                    onClick={() => setData({ ...data, accountabilityAreas: data.accountabilityAreas.filter((_, index) => index !== i) })}
                    icon={<Icon w="14px" h="14px" viewBox="0 0 24 24" fill="none"><Trash04Icon /></Icon>}
                  />
                </Flex>
              ))}
            </Box>
          )}
          <Flex w="100%" gap="8px" alignItems="stretch">
            <Box flex="2">
              <ModalSelect
                label={t('classifications.sections.accountabilityAreas')}
                name="accountabilityArea"
                value={currentArea.areaId || ''}
                options={
                  (areasQuery.data?.data || [])
                    .filter((a: { id: string; title: string }) => !data.accountabilityAreas.some((da) => da.areaId === a.id))
                    .map((a: { id: string; title: string }) => ({ value: a.id, label: a.title }))
                }
                onChange={(e) => {
                  const areaId = e.target.value
                  if (!areaId) {
                    setCurrentArea({ ...currentArea, areaId: null, areaName: null })
                    return
                  }
                  const area = areasQuery.data?.data?.find((a: { id: string }) => a.id === areaId)
                  if (area) {
                    setCurrentArea({ ...currentArea, areaId: area.id, areaName: area.title })
                    setAreaErrors({ ...areaErrors, areaId: false })
                  }
                }}
                placeholder={t('common.placeholders.selectOption')}
                onSearch={(v) => setAreaSearch(v)}
                isLoading={areasQuery.isLoading}
                isInvalid={areaErrors.areaId}
              />
              {areaErrors.areaId && (
                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                  <Text>
                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                      <WarningIcon />
                    </Icon>
                    {t('classifications.validation.invalidSelection')}
                  </Text>
                </Box>
              )}
            </Box>
            <Box flex="1">
              <ModalMultiSelect
                label={t('classifications.sections.raciLabels')}
                options={[
                  { key: 'responsible', name: t('classifications.raci.responsible') },
                  { key: 'accountable', name: t('classifications.raci.accountable') },
                  { key: 'consulted', name: t('classifications.raci.consulted') },
                  { key: 'informed', name: t('classifications.raci.informed') },
                ]}
                selectedValues={currentArea.raciLabels.map((l) => ({
                  key: l,
                  name: t(`classifications.raci.${l}`),
                }))}
                onChange={(selected) => {
                  setCurrentArea({ ...currentArea, raciLabels: selected.map((s) => s.key) })
                  if (selected.length > 0) setAreaErrors({ ...areaErrors, raciLabels: false })
                }}
                searchDisabled
                placeholder={t('common.placeholders.selectOption')}
                isInvalid={areaErrors.raciLabels}
              />
              {areaErrors.raciLabels && (
                <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
                  <Text>
                    <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
                      <WarningIcon />
                    </Icon>
                    {t('classifications.validation.selectAtLeastOne')}
                  </Text>
                </Box>
              )}
            </Box>
            <Button
              aria-label={t('common.actions.add')}
              variant="mono"
              minW="40px"
              px={0}
              mt="22px"
              onClick={handleAddArea}
            >
              <Icon w="20px" h="20px" viewBox="0 0 24 24"><PlusIcon /></Icon>
            </Button>
          </Flex>
        </Box>
      </VStack>
      <VStack spacing={3} align="stretch" w="100%">
        <Box width="100%">
          <Text textStyle="h-ov" color="solid.600" borderBottomColor="solid.50" borderBottomWidth="1px" mt={3}>
            {t('classifications.sections.salary').toUpperCase()}
          </Text>
          {data.countries.map((item, idx) => (
            <Flex key={idx} gap={4} alignItems="center" mb={2} mt={2}>
              <Box flex="1">
                <ModalSelect
                  label={t('common.fields.country')}
                  name={`country-${idx}`}
                  value={item.country}
                  options={countrySelectOptions}
                  showCountryFlag
                  onChange={(e) => {
                    const newCountry = e.target.value
                    setData((prev) => {
                      const newCountries = [...prev.countries]
                      const resolvedCurrency = currencyMap[newCountry] || ''
                      newCountries[idx] = { ...newCountries[idx], country: newCountry, currency: resolvedCurrency }
                      return { ...prev, countries: newCountries }
                    })
                  }}
                />
              </Box>
              <Box flex="1">
                <ModalInput
                  label={`${t('classifications.salary.fields.minimumSalary')}${item.currency ? ` ${currencySymbolMap(item.currency) || item.currency}` : ''}`}
                  type="number"
                  name={`minimum_salary-${idx}`}
                  value={item.minimum_salary}
                  onChange={(e) => {
                    const newMin = e.target.value
                    setData((prev) => {
                      const newCountries = [...prev.countries]
                      newCountries[idx] = { ...newCountries[idx], minimum_salary: newMin }
                      return { ...prev, countries: newCountries }
                    })
                  }}
                  placeholder="xxxxxx"
                />
              </Box>
              <Box flex="1">
                <ModalInput
                  label={`${t('classifications.salary.fields.maximumSalary')}${item.currency ? ` ${currencySymbolMap(item.currency) || item.currency}` : ''}`}
                  type="number"
                  name={`maximum_salary-${idx}`}
                  value={item.maximum_salary}
                  onChange={(e) => {
                    const newMax = e.target.value
                    setData((prev) => {
                      const newCountries = [...prev.countries]
                      newCountries[idx] = { ...newCountries[idx], maximum_salary: newMax }
                      return { ...prev, countries: newCountries }
                    })
                  }}
                  placeholder="xxxxxx"
                />
              </Box>
              <Box alignSelf="center">
                <IconButton
                  mt={5}
                  ml={2}
                  onClick={() => {
                    setData((prev) => ({
                      ...prev,
                      countries: prev.countries.filter((_, index) => index !== idx),
                    }))
                  }}
                  aria-label={t('common.actions.remove')}
                  variant="error"
                  icon={
                    <Icon w="24px" h="24px" viewBox="0 0 24 24" fill="none">
                      <Trash04Icon />
                    </Icon>
                  }
                />
              </Box>
            </Flex>
          ))}
          <Button
            variant="examples"
            mt={4}
            onClick={() => {
              setData((prev) => ({
                ...prev,
                countries: [...prev.countries, { country: '', minimum_salary: '', maximum_salary: '' }],
              }))
            }}
          >
            <Icon w="20px" h="20px" viewBox="0 0 24 24" fill="primary.100" color="primary.100" mr={2}>
              <PlusIcon />
            </Icon>
            {t('classifications.actions.addCountrySalary')}
          </Button>
        </Box>
      </VStack>
    </FormModal>
  )
}

export default AddJobsModal
